import { Box, Grid } from "@mui/material";
import Typography from "components/common/Typography";
import { debounce, isEmpty } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Mention, MentionsInput } from "react-mentions";
import { useAppSelector } from "redux/hooks";
import { instance } from "utils/APIHelper";
import { messages, minSearchStrLength, PAGE_SIZE } from "utils/CommonConstants";
import { createParamforSearch, getLoggedInUserHltId } from "utils/CommonFunctions";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { USER_SERVICE } from "utils/URLConstants";
import "./Comments.scss";

export const EVERYONE_MENTION = {
  id: "everyone",
  display: "Everyone"
};

export const MentionInput = (props: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const innCode = useAppSelector((state: any) => state.myPlanDetails.myPlanDetails.innCode);
  const isCorp = useAppSelector((state) => state.app.loggedInUserDetails.isCorp);
  const [showNoResultsMsg, setShowNoResultsMsg] = useState(false);

  const hltId = getLoggedInUserHltId();

  const handleChange = (e: any, v: any) => {
    setShowNoResultsMsg(false);
    props.setCommentValue(v);
  };

  const fetchUsers = useCallback(
    debounce((query: string, callback: (data: any[]) => void) => {
      if (query.length > minSearchStrLength) {
        setIsLoading(true);
        const URL = `${USER_SERVICE}associates?page=0&size=${PAGE_SIZE}&searchText=${query}&innCodes=${createParamforSearch(
          isCorp,
          innCode
        )}`;

        instance
          .get(URL)
          .then((res) => {
            setIsLoading(false);
            const users = isEmpty(res.data.users)
              ? []
              : res.data.users
                  .filter((user: any) => user.hltId !== hltId)
                  .map((user: any) => ({
                    display: `${getUserNameWithConditionalCheck(
                      user.displayName,
                      user.firstName,
                      user.lastName,
                      user.email
                    )}`,
                    id: user.hltId
                  }));

            isEmpty(users) ? setShowNoResultsMsg(true) : setShowNoResultsMsg(false);

            // 👇 prepend @everyone
            callback([...users, EVERYONE_MENTION]);
          })
          .catch((error) => {
            setIsLoading(false);
            setShowNoResultsMsg(true);
            console.log("Error", error);
          });
      }
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      fetchUsers.cancel();
    };
  }, [fetchUsers]);

  return (
    <Grid container>
      <Box sx={{ width: "100%" }}>
        <MentionsInput
          value={props.commentValue}
          onChange={handleChange}
          className="mentions-input"
          allowSuggestionsAboveCursor
          placeholder="Search any user by typing `@` followed by at least three chars"
        >
          <Mention
            appendSpaceOnAdd
            markup="[{~__display__~}][~~__id__~~]"
            displayTransform={(id: any, display: any) => {
              return id === EVERYONE_MENTION.id ? `@${EVERYONE_MENTION.display}` : `@${display.split("(")[0]}`;
            }}
            trigger="@"
            data={fetchUsers}
            renderSuggestion={(suggestion, search, highlightedDisplay) => {
              const helperText = props.isCardLevelComments
                ? " (Notifies all Members assigned to the Action)"
                : " (Notifies all Plan Members)";
              return (
                <span>
                  {highlightedDisplay}
                  {suggestion.id === EVERYONE_MENTION.id ? helperText : ""}
                </span>
              );
            }}
          />
        </MentionsInput>
      </Box>

      {isLoading ? (
        <Typography variant={"label"}>Loading...</Typography>
      ) : (
        showNoResultsMsg && (
          <Box sx={{ padding: "8px" }}>
            <Typography component={"div"} variant={"label"}>
              {messages.userNotFound}
            </Typography>
          </Box>
        )
      )}
    </Grid>
  );
};
